import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ONBOARDING_STEPS, OnboardingStep } from "./constants";

const PANEL_GRADIENT_SRC = "/assets/onboarding/onboarding-gradient-full.svg";
const SWAP_MS = 300;

// A new overlay waits out the previous one, then slides in. Callers that
// should land with that intro use the full cycle.
export const OVERLAY_INTRO_MS = SWAP_MS * 2;

interface Props {
  title: string;
  subtitle?: string;
  step?: OnboardingStep;
  children: ReactNode;
  preview?: ReactNode;
  previewKey?: string;
  previewOverlay?: ReactNode;
  overlayKey?: string;
  reveal?: boolean;
  onBack?: () => void;
  onContinue: () => void;
  onSkip?: () => void;
  continueDisabled?: boolean;
  continueLabel?: string;
  notice?: string | null;
  footerNote?: ReactNode;
}

function SlideSwap({ swapKey, children, className }: { swapKey: string; children: ReactNode; className?: string }) {
  const [shownKey, setShownKey] = useState(swapKey);
  const [frozen, setFrozen] = useState(children);
  const [visible, setVisible] = useState(true);
  const nextChildren = useRef(children);
  nextChildren.current = children;

  useEffect(() => {
    if (swapKey === shownKey)
      return;
    setVisible(false);
    const timer = setTimeout(() => {
      setShownKey(swapKey);
      setFrozen(nextChildren.current);
      setVisible(false);
      requestAnimationFrame(() => setVisible(true));
    }, SWAP_MS);
    return () => clearTimeout(timer);
  }, [swapKey, shownKey]);

  const display = swapKey === shownKey ? children : frozen;
  if (!display)
    return null;

  return (
    <div className={`transition-all duration-300 ease-out transform ${visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"} ${className ?? ""}`}>
      {display}
    </div>
  );
}

function OnboardingLayout(props: Props) {
  const { t } = useTranslation();
  const { title, subtitle, step, children, preview, previewKey = "graphic", previewOverlay, overlayKey = "", reveal, onBack, onContinue, onSkip, continueDisabled, continueLabel, notice, footerNote } = props;

  return (
    <div className="flex w-screen h-screen bg-white">
      <div className="flex flex-col w-full min-w-0 p-8 lg:pt-14 lg:pr-10">
        <div className="flex flex-col flex-1 min-h-0 w-full max-w-lg mx-auto">
          {step && (
            <div className="w-full h-1 mb-6 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
              <div className="h-full bg-blue rounded-full transition-all duration-300" style={{ width: `${(step / ONBOARDING_STEPS.REVEAL) * 100}%` }} />
            </div>
          )}

          <h1 className="font-gilroy font-semibold text-gray-600 text-3xl lg:text-4xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 font-gilroy font-semibold text-gray-400 text-base">
              {subtitle}
            </p>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto mt-6 pb-4">
            {children}
          </div>

          {notice && <p className="mb-3 font-gilroy font-semibold text-sm text-red">{notice}</p>}
          {footerNote && <div className="mb-1 font-gilroy font-semibold text-sm">{footerNote}</div>}

          <div className="flex items-center gap-3 pt-4 pb-2 flex-shrink-0">
            {onBack && (
              <button type="button" className="btn btn-gray min-w-[120px]" onClick={onBack}>
                {t("Back")}
              </button>
            )}
            <button type="button" className="btn btn-blue min-w-[120px]" disabled={continueDisabled} onClick={onContinue}>
              {continueLabel ?? t("Continue")}
            </button>
            {onSkip && (step ?? 0) <= ONBOARDING_STEPS.QUESTIONS &&
              <button type="button" className="ml-auto font-semibold text-gray-400 hover:text-gray-600" onClick={onSkip}>
                {t("Skip Onboarding")}
              </button>
            }
          </div>
        </div>
      </div>

      <div className="relative hidden lg:flex h-full w-7/12 flex-shrink-0 overflow-hidden">
        <div
          className="absolute inset-y-6 right-6 left-0 rounded-3xl overflow-hidden border border-black/5 bg-no-repeat transition-all duration-[3000ms] ease-in-out"
          style={{
            backgroundImage: `url(${PANEL_GRADIENT_SRC})`,
            backgroundSize: "100% auto",
            backgroundPosition: reveal ? "center bottom" : "center top",
          }}
        >
          <SlideSwap swapKey={previewKey} className="absolute inset-0 pointer-events-none">
            {preview}
          </SlideSwap>
          <div className="absolute left-10 bottom-9 w-[47%] z-10 pointer-events-none">
            <SlideSwap swapKey={overlayKey} className="pointer-events-auto">
              {previewOverlay}
            </SlideSwap>
          </div>
        </div>
        {!preview && (
          <img
            src="https://static.portant.co/login-marketing.svg"
            alt=""
            aria-hidden
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-auto max-w-none max-h-[90%] pointer-events-none select-none"
          />
        )}
      </div>
    </div>
  );
}

export default OnboardingLayout;
